import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import COUNTRIES from '../../../../app/constants/countries';
import { listDistributors } from '../../distributors';
import Select from 'react-select';
import {
  listProductListings,
  updateProductListing,
  downloadProductListingExcel,
} from '../services/ProductListingCRUD';
import { ProductListingModel } from '../models/ProductListingModel';
import ProductListingModalForm from './ProductListingModalForm';
import { toast } from 'react-toastify';
import clsx from 'clsx';

enum ProductListingState {
  IDLE = 'idle',
  LOADING = 'loading',
}

const EMPTY_PRODUCT_LISTING: ProductListingModel = {
  id: null,
  distributor: null,
  product_variant: null,
  pvf_unit: null,
  pvpr: null,
  country: null,
  units_mult_factor: 8,
  is_active: true,
};
export default function ProductListingTable() {
  const intl = useIntl();
  const [distributors, setDistributors] = useState([]);
  const distributorOptions = [
    {
      value: null,
      label: intl.formatMessage({ id: 'productListing.allDistributors' }),
    },
    ...distributors.map((distributor) => ({
      value: distributor.id,
      label: distributor.name,
    })),
  ];
  const [distributor, setDistributor] = useState<number | null>(null);
  const [country, setCountry] = useState<string | null>(null);
  const [state, setState] = useState<ProductListingState>(
    ProductListingState.LOADING
  );
  const [productListings, setProductListings] = useState<ProductListingModel[]>(
    []
  );
  const [editingProductListing, setEditingProductListing] =
    useState<ProductListingModel | null>(null);
  const [exporting, setExporting] = useState(false);

  async function fetchDistributors() {
    try {
      const { data } = await listDistributors('active');
      setDistributors(data);
    } catch (error) {
      console.error('Error fetching distributors:', error);
    }
  }

  async function fetchProductListings() {
    setState(ProductListingState.LOADING);
    try {
      const { data } = await listProductListings({ distributor, country });

      setProductListings(data);
    } catch (error) {
      console.error('Error fetching product listings:', error);
    } finally {
      setState(ProductListingState.IDLE);
    }
  }

  function editProductListing(productListing: ProductListingModel) {
    setEditingProductListing(productListing);
  }

  function addProductListing() {
    setEditingProductListing(EMPTY_PRODUCT_LISTING);
  }

  function onUpdated() {
    setEditingProductListing(null);
    fetchProductListings();
  }

  async function toggleIsActive(productListing: ProductListingModel) {
    try {
      const modifiedProductListing = {
        id: productListing.id,
        country: productListing.country.code,
        distributor: productListing.distributor?.id || null,
        pvf_unit: productListing.pvf_unit,
        pvpr: productListing.pvpr,
        units_mult_factor: productListing.units_mult_factor,
        product: productListing.product_variant.product_variant_id,
        is_active: !productListing.is_active,
      };
      await updateProductListing(modifiedProductListing);
      // Reload instead of patching the row: switching a tariff on switches
      // off the one it replaces, which is another row.
      fetchProductListings();
      toast.success(
        intl.formatMessage({ id: 'productListing.toggleActiveSuccess' })
      );
    } catch (error) {
      toast.error(
        intl.formatMessage({ id: 'productListing.toggleActiveError' })
      );
    }
  }

  async function onDownloadExcel() {
    try {
      setExporting(true);
      const blob = await downloadProductListingExcel({ distributor, country });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `product-listing-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(intl.formatMessage({ id: 'productListing.exportSuccess' }));
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'productListing.exportError' }));
    } finally {
      setExporting(false);
    }
  }

  useEffect(() => {
    fetchDistributors();
  }, []);

  useEffect(() => {
    fetchProductListings();
  }, [distributor, country]);

  return (
    <>
      <div className="card p-10 w-100 mw-900px">
        <h1 className="mb-8">
          {intl.formatMessage({ id: 'productListing.title' })}
        </h1>
        <div className="d-flex gap-4 align-items-center">
          <div className="form-group">
            <label htmlFor="country" className="form-label">
              {intl.formatMessage({ id: 'productListing.filterByCountry' })}
            </label>
            <select
              id="country"
              className="form-select"
              value={country}
              onChange={(e) =>
                e.target.value.length > 0
                  ? setCountry(e.target.value)
                  : setCountry(null)
              }
            >
              <option value="">
                {intl.formatMessage({ id: 'productListing.allCountries' })}
              </option>
              {COUNTRIES.map((country) => (
                <option key={country} value={country}>
                  {intl.formatMessage({
                    id: `productListing.${country.toLowerCase()}`,
                  })}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="distributor" className="form-label">
              {intl.formatMessage({ id: 'productListing.filterByDistributor' })}
            </label>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={distributorOptions}
              value={distributorOptions.find(
                (option) => option.value === distributor
              )}
              onChange={(newValue) => setDistributor(newValue.value)}
              placeholder={intl.formatMessage({
                id: 'productListing.allDistributors',
              })}
            />
          </div>
          {state === ProductListingState.LOADING && (
            <div
              className="spinner-border spinner-border-sm text-primary mt-8 ms-4"
              role="status"
            />
          )}
          <button
            className="btn btn-success mt-8 ms-auto"
            onClick={onDownloadExcel}
            disabled={exporting || !productListings?.length}
          >
            <span className="fas fa-file-excel me-2"></span>
            {intl.formatMessage({ id: 'productListing.exportExcel' })}
          </button>
          <button className="btn btn-primary mt-8" onClick={addProductListing}>
            <span className="fa fa-plus me-2"></span>
            {intl.formatMessage({ id: 'productListing.addProductListing' })}
          </button>
        </div>
        {state === ProductListingState.IDLE && !productListings?.length && (
          <p className="mt-8">
            {intl.formatMessage({ id: 'productListing.noProductListings' })}
          </p>
        )}
        {state === ProductListingState.IDLE && productListings?.length > 0 && (
          <div className="table-responsive mt-8">
            <table className="table table-striped">
              <thead>
                <tr className="fw-bolder">
                  <th>
                    {intl.formatMessage({ id: 'productListing.isActive' })}
                  </th>
                  <th>
                    {intl.formatMessage({ id: 'productListing.country' })}
                  </th>
                  <th>
                    {intl.formatMessage({ id: 'productListing.distributor' })}
                  </th>
                  <th>
                    {intl.formatMessage({ id: 'productListing.product' })}
                  </th>
                  <th>{intl.formatMessage({ id: 'productListing.ean' })}</th>
                  <th>{intl.formatMessage({ id: 'productListing.price' })}</th>
                  <th>{intl.formatMessage({ id: 'productListing.pvpr' })}</th>
                  <th>
                    {intl.formatMessage({
                      id: 'productListing.unitsMultFactor',
                    })}
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {productListings.map((productListing) => (
                  <tr
                    key={productListing.id}
                    className={clsx({
                      'align-middle': true,
                      'opacity-50': !productListing.is_active,
                    })}
                  >
                    <td>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={productListing.is_active}
                          onChange={() => toggleIsActive(productListing)}
                        />
                      </div>
                    </td>
                    <td>{productListing.country.name_es}</td>
                    <td>
                      {productListing?.distributor?.name ||
                        intl.formatMessage({
                          id: 'productListing.countryDefaultPrice',
                        })}
                    </td>
                    <td>
                      {productListing.product_variant.product_variant_name}
                    </td>
                    <td>
                      {productListing.product_variant.product_variant_ean ||
                        '-'}
                    </td>
                    <td>{productListing.pvf_unit}€</td>
                    <td>
                      {productListing.pvpr != null
                        ? `${productListing.pvpr}€`
                        : '-'}
                    </td>
                    <td className="text-center">
                      {productListing.units_mult_factor}
                    </td>
                    <td>
                      <button
                        className="btn btn-primary btn-sm px-3 py-2 text-nowrap"
                        onClick={() => editProductListing(productListing)}
                      >
                        <span className="fa fa-edit me-2"></span>
                        {intl.formatMessage({ id: 'productListing.edit' })}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ProductListingModalForm
        distributors={distributors}
        productListing={editingProductListing}
        onClose={() => setEditingProductListing(null)}
        onUpdated={onUpdated}
        visible={!!editingProductListing}
      />
    </>
  );
}
